import React, { useRef, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Stack, Inline, Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import Spinner from '@atlaskit/spinner';
import VideoIcon from '@atlaskit/icon/core/video';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';

import { useLoadVideoDurations } from './hooks/useLoadVideoDurations';
import type { ContentFeatures } from './featuresExtractor';
import { calculateImageReadTime } from './readTimeUtils';

export const i18n = defineMessages({
	readTime: {
		id: 'read-time.read-time-popup',
		defaultMessage: 'Read time break-down',
		description: 'Read time label',
	},
	loomVideos: {
		id: 'read-time.read-time-popup-loom-videos',
		defaultMessage: 'Looms',
		description: 'Loom videos label',
	},
	pageContent: {
		id: 'read-time.read-time-popup-page-content',
		defaultMessage: 'Page content',
		description: 'Page content label',
	},
	expands: {
		id: 'read-time.read-time-popup-expands',
		defaultMessage: 'Expands',
		description: 'Expands label',
	},
	expandTooltipDescription: {
		id: 'read-time.read-time-popup-expand-tooltip-description',
		defaultMessage: 'Loom videos and expands are excluded from read time estimates',
		description: 'Expand tooltip message',
	},
	readTimeDisclaimer: {
		id: 'read-time.read-time-popup-read-time-disclaimer',
		defaultMessage: 'Not included in read time',
		description: 'Read time disclaimer',
	},
});

const popupStyles = xcss({
	width: '340px',
	position: 'relative',
	backgroundColor: 'elevation.surface.raised',
	border: `1px solid color.border`,
	borderRadius: 'border.radius.200',
	padding: 'space.250',
});

const headingContainerStyles = xcss({
	marginTop: 'space.negative.050',
	marginBottom: 'space.200',
});

const featureContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const lastFeatureContainerStyles = xcss({
	marginBottom: 'space.0',
});

const baseIconStyles = xcss({
	paddingBlockStart: 'space.025',
	marginRight: 'space.100',
});

const expandIconStyles = xcss({
	borderRadius: 'border.radius.100',
	border: `1px solid ${token('color.border')}`,
	width: '20px',
	height: '20px',
	marginLeft: 'space.negative.025',
	marginRight: 'space.075',
});

const HorizontalDivider = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	height: '1px',
	width: '135px',
});

const formatLoomDuration = (ms: number): string => {
	const minutes = Math.floor(ms / 60000);
	const seconds = Math.round((ms % 60000) / 1000);

	if (minutes === 0) {
		return `${seconds} sec`;
	}
	if (seconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${seconds} sec`;
};

const formatReadTimeInSeconds = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);

	if (minutes === 0) {
		return `${remainingSeconds} sec`;
	}
	if (remainingSeconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${remainingSeconds} sec`;
};

export type ReadTimePopupProps = {
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	source: string;
};
export const ReadTimePopup = ({ features, featuresSkipExpand, source }: ReadTimePopupProps) => {
	const { videoDurations, isLoadingDurations } = useLoadVideoDurations(features?.videos || []);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readTimePopupRef = useRef<HTMLDivElement>(null);
	const { getDwellTime } = usePopupDwellTime(readTimePopupRef);

	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'readTime',
					actionSubjectId: 'readTimePopup',
					source,
					attributes: {
						dwellTime: getDwellTime(),
					},
				},
			}).fire();
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!features && !featuresSkipExpand) {
		return null;
	}

	let expandsReadTime = 0;
	if (features && featuresSkipExpand) {
		if (features.wordCount !== featuresSkipExpand.wordCount) {
			expandsReadTime = ((features.wordCount - featuresSkipExpand.wordCount) / 265) * 60;
		}
	}

	const readTimeWithoutExpands = ((featuresSkipExpand?.wordCount || 0) / 265) * 60;
	const pageContentReadTime =
		readTimeWithoutExpands + calculateImageReadTime(featuresSkipExpand?.imageCount || 0);

	return (
		<Box ref={readTimePopupRef} testId="read-time-popup">
			<Stack xcss={popupStyles}>
				<Box key="readTime" xcss={[featureContainerStyles, headingContainerStyles]}>
					<Inline alignBlock="center" spread="space-between">
						<Heading size="medium">
							<FormattedMessage {...i18n.readTime} />
						</Heading>
					</Inline>
				</Box>
				<Box key="wordCountSkipExpand" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Box xcss={baseIconStyles}>
								<AlignTextLeftIcon color={token('color.text.subtle')} label="Page read time" />
							</Box>
							<FormattedMessage {...i18n.pageContent} />
						</Inline>
						<Text>{formatReadTimeInSeconds(pageContentReadTime)}</Text>
					</Inline>
				</Box>
				<Box key="readTimeDisclaimer" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text.subtlest" size="small">
							<FormattedMessage {...i18n.readTimeDisclaimer} />
						</Text>
						<Box xcss={HorizontalDivider} />
					</Inline>
				</Box>
				<Box key="loomDuration" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Box xcss={baseIconStyles}>
								<VideoIcon color={token('color.text.subtle')} label="Loom videos" />
							</Box>
							<FormattedMessage {...i18n.loomVideos} />
						</Inline>
						{isLoadingDurations ? (
							<Spinner size="small" />
						) : (
							<Text>{formatLoomDuration(videoDurations)}</Text>
						)}
					</Inline>
				</Box>
				<Box key="expandsReadTime" xcss={[featureContainerStyles, lastFeatureContainerStyles]}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Flex alignItems="center" justifyContent="center" xcss={expandIconStyles}>
								<ChevronRightIcon
									color={token('color.text.subtle')}
									label="Expands"
									spacing="spacious"
								/>
							</Flex>
							<FormattedMessage {...i18n.expands} />
						</Inline>
						<Text>{formatReadTimeInSeconds(expandsReadTime)} </Text>
					</Inline>
				</Box>
			</Stack>
		</Box>
	);
};

export const ReadTimePopupWithErrorBoundary = (props: ReadTimePopupProps) => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<ReadTimePopup {...props} />
		</ErrorBoundary>
	);
};
